import React, { useEffect, useState } from 'react';
import { bool, node } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { Form, PrimaryButton, FieldTextInput, FieldSelect, CountrySelector } from '../../../components';

import css from './ConfirmSignupForm.module.css';
import { phoneNumberValidator } from '../../../util/phoneValidation';

const ConfirmSignupFormComponent = props => {
  const [countryCode, setCountryCode] = useState({ code: props.country || 'US' });
  const [lastCountryFieldValue, setLastCountryFieldValue] = useState(props.country || 'US');

  useEffect(() => {
    setCountryCode({ code: props.country || 'US' });
    setLastCountryFieldValue(props.country || 'US');
  }, [props.country]);

  // ✅ Get language from localStorage or default to 'en'
  const lang =
    typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem('appLanguage') || 'en'
      : 'en';

  // ✅ Use Intl.DisplayNames if available for localized country names
  const countryDisplayNames =
    typeof Intl !== 'undefined' && Intl.DisplayNames
      ? new Intl.DisplayNames([lang], { type: 'region' })
      : null;

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      keepDirtyOnReinitialize={true}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          inProgress,
          invalid,
          intl,
          termsAndConditions,
          authInfo,
          idp,
          values,
          signupCountries,
          form: formApi,
          showAlmostThereMessage
        } = formRenderProps;

        const countryLabel = intl.formatMessage({ id: "SignupForm.countryLabel" });

        // email
        const emailRequired = validators.required(
          intl.formatMessage({
            id: 'ConfirmSignupForm.emailRequired',
          })
        );
        const emailValid = validators.emailFormatValid(
          intl.formatMessage({
            id: 'ConfirmSignupForm.emailInvalid',
          })
        );

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = invalid || submitInProgress;

        // If authInfo is not available we should not show the ConfirmForm
        if (!authInfo) {
          return;
        }

        // Initial values from idp provider
        const { email, firstName, lastName } = authInfo;

        const formSpyChangeHandler = ({ values, dirty }) => {
          // Only update countryCode if the country FIELD itself changed
          // Not just because countryCode and country field are different
          if (values && values.country && values.country !== lastCountryFieldValue && dirty) {
            setLastCountryFieldValue(values.country);
            setCountryCode({ code: values.country });
            formApi.change('phoneNumber', null);
          }
        };

        // Phone validation - match SignupForm structure
        const validatePhoneNumber = phoneNumberValidator(
          intl.formatMessage({ id: 'SignupForm.phoneNumberInvalid' }),
          countryCode.code
        );
        const phoneNumberRequired = validators.required(
          intl.formatMessage({ id: 'SignupForm.phoneNumberRequired' })
        );
        const phoneNumberValid = validators.composeValidators(
          phoneNumberRequired,
          validatePhoneNumber
        );

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <FormSpy onChange={formSpyChangeHandler} subscription={{ values: true, dirty: true }} />
            <div>
              {/* Name Fields */}
              <div className={css.formSection}>
                <div className={css.nameRow}>
                  <FieldTextInput
                    className={css.nameField}
                    type="text"
                    id={formId ? `${formId}.firstName` : 'firstName'}
                    name="firstName"
                    autoComplete="given-name"
                    label={intl.formatMessage({
                      id: 'SignupForm.firstNameLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.firstNamePlaceholder',
                    })}
                    initialValue={firstName}
                    validate={validators.required(
                      intl.formatMessage({
                        id: 'SignupForm.firstNameRequired',
                      })
                    )}
                    iconLeft={<i className='ri-user-line'></i>}
                  />
                  <FieldTextInput
                    className={css.nameField}
                    type="text"
                    id={formId ? `${formId}.lastName` : 'lastName'}
                    name="lastName"
                    autoComplete="family-name"
                    label={intl.formatMessage({
                      id: 'SignupForm.lastNameLabel',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'SignupForm.lastNamePlaceholder',
                    })}
                    initialValue={lastName}
                    validate={validators.required(
                      intl.formatMessage({
                        id: 'SignupForm.lastNameRequired',
                      })
                    )}
                    iconLeft={<i className='ri-user-line'></i>}
                  />
                </div>
                <small className={css.note}>
                  <FormattedMessage
                    id="SignupForm.nameNote"
                    defaultMessage="Make sure your first and last name matches your government-issued ID."
                  />
                </small>
              </div>

              {/* Country Field */}
              <div className={css.marginBottom}>
                <FieldSelect
                  id="country"
                  name="country"
                  className={css.selectCountry}
                  autoComplete="country"
                  label={countryLabel}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.countryRequiredToContinue',
                    })
                  )}
                  srOnly={true}
                >
                  <option disabled className={css.countryPlaceholder} value="">
                    {intl.formatMessage({ id: 'SignupForm.countryPlaceholder' })}
                  </option>
                  {signupCountries.map(c => (
                    <option key={c.code} value={c.code}>
                      {countryDisplayNames
                        ? countryDisplayNames.of(c.code)
                        : c.country}
                    </option>
                  ))}
                </FieldSelect>
              </div>

              {/* Phone Field - Optional */}
              <div className={css.formSection}>
                <div className={css.phoneWrapper}>
                  <div className={css.phoneInput}>
                    <CountrySelector
                      key={countryCode.code}
                      options={signupCountries}
                      onSelect={v => {
                        setCountryCode({ code: v });
                        formApi.change('phoneNumber', null);
                      }}
                      currentUserCountry={countryCode}
                      rootClassName={css.dropdown}
                    />
                    <FieldTextInput
                      className={css.phoneNumber}
                      type="tel"
                      id={`${formId}.phoneNumber`}
                      name="phoneNumber"
                      label={intl.formatMessage({
                        id: 'SignupForm.phoneLabel',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'SignupForm.phonePlaceholder',
                      })}
                      validate={phoneNumberValid}
                      iconLeft={<i className="ri-phone-line"></i>}
                    />
                  </div>
                  <small className={css.note}>
                    <FormattedMessage
                      id="SignupForm.phoneNote"
                      defaultMessage="Required for rental notifications and support."
                    />
                  </small>
                </div>
              </div>

              {/* Email */}
              <div className={css.formSection}>
                <FieldTextInput
                  type="email"
                  id={formId ? `${formId}.email` : 'email'}
                  name="email"
                  autoComplete="email"
                  label={intl.formatMessage({
                    id: 'SignupForm.emailLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.emailPlaceholder',
                  })}
                  initialValue={email}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                  iconLeft={<i className='ri-mail-line'></i>}
                />
                <small className={css.note}>
                  <FormattedMessage
                    id="SignupForm.emailNote"
                    defaultMessage="We will email booking information and messages"
                  />
                </small>
              </div>

            </div>

            <div className={css.bottomWrapper}>
              {termsAndConditions}
              <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
                <FormattedMessage id="ConfirmSignupForm.signUp" values={{ idp: idp }} />
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

ConfirmSignupFormComponent.defaultProps = { inProgress: false };

ConfirmSignupFormComponent.propTypes = {
  inProgress: bool,
  termsAndConditions: node.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const ConfirmSignupForm = compose(injectIntl)(ConfirmSignupFormComponent);
ConfirmSignupForm.displayName = 'ConfirmSignupForm';

export default ConfirmSignupForm;
